/**
 * PlatformPostsTable — per-platform corpus table with multi-select + bulk
 * delete + sortable date column.
 *
 * Replaces the legacy global `Content corpus` block (AuthorContentCorpus.tsx)
 * with a platform-scoped table that lives inside the Posts tab of
 * PlatformsSection. Per-platform scope makes bulk-actions unambiguous when
 * multiple platforms are enabled.
 *
 * Built on top of @modules/ui-components SDataTable (MRT v3 wrapper) — same
 * primitive used by TasksTable, StudyCasesTable, etc. Matches the
 * DataTableWithBulkActions storybook pattern.
 *
 * Toolbar actions:
 *   • «Scrape more» (if profileUrl exists) — fires authorScrape mode
 *   • «Re-analyze voice» — opens ConfirmActionDialog with an available AI model selector
 *
 * Per-row actions:
 *   • Open original (link out)
 *   • Delete (single)
 *
 * Bulk header (when ≥1 selected):
 *   • Selected count chip
 *   • Delete selected (icon button)
 *   • Clear selection (button)
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import TwitterIcon from '@mui/icons-material/Twitter';
import RedditIcon from '@mui/icons-material/Reddit';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components';
import { useAuthorContentCorpus, type AuthorPostLite } from '../../hooks/useAuthorContentCorpus';
import { getAuthorContentCorpusService } from '../../services/AuthorContentCorpusService';
import type { AuthorBasePath } from '../../services/AuthorPlatformService';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { ConfirmActionDialog, type ConfirmActionResult } from '../author/ConfirmActionDialog';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import { AuthorPostEditorDialog } from '../posts/AuthorPostEditorDialog';
import type { Post } from '../../types';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { getXTwitterConnectService } from '@modules/x-twitter-connect';
import { getRedditConnectService } from '@modules/reddit-connect';

const TESTID = 'platform-posts-table';
const X_TWITTER_OAUTH_SESSION_KEY = 'x_twitter_oauth_in_progress';
const REDDIT_OAUTH_SESSION_KEY = 'reddit_oauth_in_progress';

export interface PlatformPostsTableProps {
  projectId: string;
  authorId: string;
  platformId: string;
  platformName: string;
  /** Author's URL on this platform (from Platform.profileUrl). Drives Scrape more. */
  profileUrl?: string;
  /** User id required for partial-mode endpoints. */
  userId: string;
  /** Discriminates self-author vs tracked creator. Default `'authors'`. */
  basePath?: AuthorBasePath;
  /** Notified when scrape/analyze task starts. */
  onTaskStarted?: (taskId: string, label: string) => void;
}

const formatDate = (raw: any): string => {
  if (!raw) return '—';
  // Firestore Timestamp shape: { _seconds, _nanoseconds } OR Date OR ISO string
  const d =
    raw?._seconds != null
      ? new Date(raw._seconds * 1000)
      : raw instanceof Date
      ? raw
      : new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const truncate = (s: string | undefined, max = 120): string => {
  if (!s) return '';
  return s.length > max ? `${s.slice(0, max).trim()}…` : s;
};

const isTwitterPlatformValue = (platformId: string, platformName: string): boolean => {
  const id = platformId.toLowerCase();
  const name = platformName.toLowerCase();
  return (
    id === 'twitter' ||
    id === 'x' ||
    id === 'x-twitter' ||
    name.includes('twitter') ||
    /\bx\s*\/\s*twitter\b/.test(name) ||
    /\btwitter\s*\/\s*x\b/.test(name)
  );
};

const isRedditPlatformValue = (platformId: string, platformName: string): boolean => {
  const id = platformId.toLowerCase();
  const name = platformName.toLowerCase();
  return id === 'reddit' || name.includes('reddit');
};

const isXAuthError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /not connected|reconnect|required|authorization|auth/i.test(message);
};

const isRedditAuthError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /not connected|reconnect|required|authorization|auth/i.test(message);
};

export const PlatformPostsTable: React.FC<PlatformPostsTableProps> = ({
  projectId,
  authorId,
  platformId,
  platformName,
  profileUrl,
  userId,
  basePath = 'authors',
  onTaskStarted,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const corpusService = useMemo(
    () => getAuthorContentCorpusService(basePath),
    [basePath],
  );
  const { posts, loading } = useAuthorContentCorpus(
    projectId,
    authorId,
    platformId,
    500,
    basePath,
  );
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [xReauthLoading, setXReauthLoading] = useState(false);
  const [redditReauthLoading, setRedditReauthLoading] = useState(false);
  const [examplesOnly, setExamplesOnly] = useState(false);
  const isTwitterPlatform = isTwitterPlatformValue(platformId, platformName);
  const isRedditPlatform = isRedditPlatformValue(platformId, platformName);
  const isNativeSyncPlatform = isTwitterPlatform || isRedditPlatform;

  // Atomic operation dialogs
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [reanalyzeOpen, setReanalyzeOpen] = useState(false);

  // Author post editor dialog — manual-add (post=null) + edit (post=Post)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPost, setEditorPost] = useState<Post | null>(null);

  const apiClient = useMemo(() => new ContentPlanApiClient(), []);

  const startXReauth = async () => {
    setError(null);
    setXReauthLoading(true);
    try {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const oauthUrl = await getXTwitterConnectService().getOAuthUrl(projectId, returnTo);
      sessionStorage.setItem(X_TWITTER_OAUTH_SESSION_KEY, projectId);
      window.location.href = oauthUrl;
    } catch (e: any) {
      setError(e?.message ?? 'Failed to start X reauthentication');
      setXReauthLoading(false);
    }
  };

  const startRedditReauth = async () => {
    setError(null);
    setRedditReauthLoading(true);
    try {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const oauthUrl = await getRedditConnectService().getOAuthUrl(projectId, returnTo);
      sessionStorage.setItem(REDDIT_OAUTH_SESSION_KEY, projectId);
      window.location.href = oauthUrl;
    } catch (e: any) {
      setError(e?.message ?? 'Failed to start Reddit reauthentication');
      setRedditReauthLoading(false);
    }
  };

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  // Aggregate distinct example-tag values across the corpus for autocomplete
  // suggestions — lets users pick existing tags rather than retyping ("обучающие",
  // "разборы", …). Falls back to free-text when the user types something new.
  const tagSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const p of posts) {
      for (const t of p.exampleForPostTypes ?? []) {
        if (typeof t === 'string' && t.trim()) set.add(t.trim());
      }
    }
    return Array.from(set).sort();
  }, [posts]);

  const displayPosts = useMemo(() => {
    if (!examplesOnly) return posts;
    return posts.filter((p) => (p.exampleForPostTypes?.length ?? 0) > 0);
  }, [posts, examplesOnly]);

  const handleTagsChange = async (postId: string, types: string[]) => {
    setError(null);
    try {
      await corpusService.setExampleForPostTypes({
        projectId,
        authorId,
        platformId,
        postId,
        types,
      });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to update example tags');
    }
  };

  // ─── Bulk delete ─────────────────────────────────────────────────────

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Delete ${selectedIds.length} post(s)? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await corpusService.deleteAuthorPosts({
        projectId,
        authorId,
        platformId,
        postIds: selectedIds,
      });
      setRowSelection({});
    } catch (e: any) {
      setError(e?.message ?? 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Per-row delete ──────────────────────────────────────────────────

  const handleDeleteOne = async (postId: string) => {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    setError(null);
    try {
      await corpusService.deleteAuthorPosts({
        projectId,
        authorId,
        platformId,
        postIds: [postId],
      });
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
    }
  };

  // ─── Atomic operations ───────────────────────────────────────────────

  const handleScrapeConfirm = async (result: ConfirmActionResult) => {
    setSyncMessage(null);
    if (isTwitterPlatform) {
      try {
        const res = await getXTwitterConnectService().syncOwnPosts({
          projectId,
          authorId,
          maxResults: result.maxPosts,
        });
        const mediaSuffix = res.mediaAssetCount
          ? ` Mirrored ${res.mediaAssetCount} media assets.`
          : '';
        setSyncMessage(`Synced ${res.writtenCount} X posts into the corpus.${mediaSuffix}`);
        return;
      } catch (e) {
        if (isXAuthError(e)) {
          setSyncMessage('X authorization expired. Redirecting to reconnect...');
          await startXReauth();
          return;
        }
        throw e;
      }
    }

    if (isRedditPlatform) {
      try {
        const res = await getRedditConnectService().syncOwnPosts({
          projectId,
          authorId,
          maxResults: result.maxPosts,
        });
        const mediaSuffix = res.mediaAssetCount
          ? ` Mirrored ${res.mediaAssetCount} media assets.`
          : '';
        setSyncMessage(`Synced ${res.writtenCount} Reddit posts into the corpus.${mediaSuffix}`);
        return;
      } catch (e) {
        if (isRedditAuthError(e)) {
          setSyncMessage('Reddit authorization expired. Redirecting to reconnect...');
          await startRedditReauth();
          return;
        }
        throw e;
      }
    }

    const res = await apiClient.authorScrape({
      projectId,
      userId,
      existingAuthorId: authorId,
      profileUrl,
      // schema 1.3.1+: extended maxPosts type to `number | 'all'`.
      // scrapeSince/scrapeUntil are LinkedIn-only date-range filters
      // forwarded to BrightData deep-posts trigger payload.
      // schema 1.4.0+: authorKind selects external_authors collection.
      ...({
        maxPosts: result.maxPosts,
        scrapeSince: result.scrapeSince,
        scrapeUntil: result.scrapeUntil,
        ...(basePath === 'external_authors'
          ? { authorKind: 'tracked-creator' }
          : {}),
      } as any),
    });
    if (!res.success || !res.taskId) {
      throw new Error(res.error ?? 'Scrape dispatch failed');
    }
    onTaskStarted?.(res.taskId, `Scrape more from ${platformName}`);
  };

  const handleReanalyzeConfirm = async (result: ConfirmActionResult) => {
    const res = await apiClient.authorAnalyze({
      projectId,
      userId,
      existingAuthorId: authorId,
      platformId,
      // schema 1.3.1+: scope='voice' only re-derives voice + post_examples
      // schema 1.4.0+: authorKind selects external_authors collection.
      ...({
        scope: 'voice',
        model: result.model,
        analysisInstructions: result.analysisInstructions,
        ...(basePath === 'external_authors'
          ? { authorKind: 'tracked-creator' }
          : {}),
      } as any),
    });
    if (!res.success || !res.taskId) {
      throw new Error(res.error ?? 'Re-analyze dispatch failed');
    }
    onTaskStarted?.(res.taskId, `Re-analyze ${platformName} voice`);
  };

  // ─── Columns ─────────────────────────────────────────────────────────

  const columns = useMemo<SDataTableColumnDef<AuthorPostLite>[]>(
    () => [
      {
        accessorKey: 'hook',
        header: 'Hook / preview',
        Cell: ({ row }) => {
          const post = row.original;
          const text = post.hook || post.body || '';
          return (
            <Tooltip title={post.body ?? text} placement="top">
              <Typography variant="body2" sx={{ maxWidth: 480 }}>
                {truncate(text, 120)}
              </Typography>
            </Tooltip>
          );
        },
        size: 480,
      },
      {
        accessorKey: 'publishedAt',
        header: 'Published',
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {formatDate(cell.getValue())}
          </Typography>
        ),
        size: 130,
        sortingFn: (a, b) => {
          const av = (a.original.publishedAt as any)?._seconds ?? 0;
          const bv = (b.original.publishedAt as any)?._seconds ?? 0;
          return av - bv;
        },
      },
      {
        accessorKey: 'engagement.likes',
        header: '👍',
        Cell: ({ row }) => {
          const n = row.original.engagement?.likes;
          return (
            <Typography variant="body2">{n != null ? n : '—'}</Typography>
          );
        },
        size: 70,
      },
      {
        accessorKey: 'engagement.comments',
        header: '💬',
        Cell: ({ row }) => {
          const n = row.original.engagement?.comments;
          return (
            <Typography variant="body2">{n != null ? n : '—'}</Typography>
          );
        },
        size: 70,
      },
      {
        accessorKey: 'engagement.shares',
        header: '🔁',
        Cell: ({ row }) => {
          const n = row.original.engagement?.shares;
          return (
            <Typography variant="body2">{n != null ? n : '—'}</Typography>
          );
        },
        size: 70,
      },
      {
        accessorKey: 'engagement.views',
        header: 'Views',
        Cell: ({ row }) => {
          const n = row.original.engagement?.views;
          return (
            <Typography variant="body2">{n != null ? n : '—'}</Typography>
          );
        },
        size: 90,
      },
      {
        accessorKey: 'source',
        header: 'Source',
        Cell: ({ cell }) => {
          const s = cell.getValue<string>();
          if (!s) return null;
          return <Chip size="small" variant="outlined" label={s} />;
        },
        size: 110,
      },
      {
        accessorKey: 'exampleForPostTypes',
        header: 'Example for',
        // Inline tag editor — multi-tag, free-solo. Suggestions sourced from
        // distinct values already used in the loaded corpus. Empty = not an
        // example. Per plan §5: AI never writes this; only humans curate.
        Cell: ({ row }) => {
          const post = row.original;
          const tags = post.exampleForPostTypes ?? [];
          return (
            <Autocomplete<string, true, false, true>
              multiple
              freeSolo
              size="small"
              options={tagSuggestions}
              value={tags}
              onChange={(_, next) => {
                if (post.id) handleTagsChange(post.id, next as string[]);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={tags.length === 0 ? 'Add tag…' : ''}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': `${TESTID}-example-tags-${post.id}`,
                  }}
                />
              )}
              sx={{ minWidth: 220 }}
            />
          );
        },
        size: 240,
        enableSorting: false,
      },
    ],
    [tagSuggestions], // re-build column when suggestions change
  );

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <Box data-testid={TESTID}>
      {/* Header: title + bulk actions or atomic-action buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">Posts</Typography>
          <Chip
            size="small"
            variant="outlined"
            label={
              examplesOnly
                ? `${displayPosts.length}/${posts.length} examples`
                : `${posts.length} total`
            }
          />
          {selectedIds.length > 0 && (
            <>
              <Chip
                label={selectedIds.length}
                size="small"
                color="primary"
                sx={{ fontWeight: 'medium' }}
                data-testid={`${TESTID}-selected-count`}
              />
              <Tooltip title="Delete selected">
                <IconButton
                  onClick={handleBulkDelete}
                  color="error"
                  size="small"
                  disabled={deleting}
                  data-testid={`${TESTID}-bulk-delete`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                onClick={() => setRowSelection({})}
                sx={{ textTransform: 'none' }}
              >
                Clear selection
              </Button>
            </>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={examplesOnly}
                onChange={(e) => setExamplesOnly(e.target.checked)}
                data-testid={`${TESTID}-examples-only-toggle`}
              />
            }
            label="Examples only"
            sx={{ mr: 0.5 }}
          />
          <Tooltip
            title={
              isTwitterPlatform
                ? 'Sync posts from the connected X account'
                : isRedditPlatform
                  ? 'Sync posts from the connected Reddit account'
                : profileUrl
                  ? 'Scrape more posts from BrightData'
                  : 'No profile URL set on this platform — re-onboard via URL or set Profile URL in Config tab'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={xReauthLoading || redditReauthLoading || (!isNativeSyncPlatform && !profileUrl)}
                onClick={() => setScrapeOpen(true)}
                data-testid={`${TESTID}-scrape-btn`}
              >
                {isTwitterPlatform ? 'Sync X posts' : isRedditPlatform ? 'Sync Reddit posts' : 'Scrape more'}
              </Button>
            </span>
          </Tooltip>
          {isTwitterPlatform && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                xReauthLoading
                  ? <CircularProgress size={16} color="inherit" />
                  : <TwitterIcon fontSize="small" />
              }
              onClick={startXReauth}
              disabled={xReauthLoading}
              data-testid={`${TESTID}-x-reauth-btn`}
            >
              {xReauthLoading ? 'Redirecting...' : 'Reconnect X'}
            </Button>
          )}
          {isRedditPlatform && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                redditReauthLoading
                  ? <CircularProgress size={16} color="inherit" />
                  : <RedditIcon fontSize="small" />
              }
              onClick={startRedditReauth}
              disabled={redditReauthLoading}
              data-testid={`${TESTID}-reddit-reauth-btn`}
            >
              {redditReauthLoading ? 'Redirecting...' : 'Reconnect Reddit'}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setReanalyzeOpen(true)}
            data-testid={`${TESTID}-reanalyze-btn`}
          >
            Re-analyze voice
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditorPost(null);
              setEditorOpen(true);
            }}
            data-testid={CONTENT_PLAN.AUTHOR_POST_EDITOR_ADD_BUTTON}
          >
            Add post manually
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {syncMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>
          {syncMessage}
        </Alert>
      )}

      {/* Table */}
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading posts…
        </Typography>
      ) : (
        <SDataTable<AuthorPostLite>
          columns={columns}
          data={displayPosts}
          getRowId={(row) => row.id || ''}
          enableRowSelection
          enableRowActions
          enableSorting
          enableTopToolbar={false}
          enablePagination
          enableBottomToolbar
          state={{ rowSelection }}
          onRowSelectionChange={setRowSelection}
          initialState={{
            sorting: [{ id: 'publishedAt', desc: true }],
            pagination: { pageIndex: 0, pageSize: 25 },
          }}
          muiTableContainerProps={{ sx: { maxHeight: 600 } }}
          muiSelectCheckboxProps={{ size: 'small' }}
          muiSelectAllCheckboxProps={{ size: 'small' }}
          muiTableBodyRowProps={({ row }) => ({
            hover: true,
            onClick: (e: React.MouseEvent) => {
              // Ignore clicks bubbling from the inline tag autocomplete,
              // checkboxes, or the kebab menu — only the row body should
              // open the editor.
              const target = e.target as HTMLElement;
              if (target.closest('input, button, [role=button], a')) return;
              setEditorPost(row.original as unknown as Post);
              setEditorOpen(true);
            },
            sx: { cursor: 'pointer' },
          })}
          renderRowActionMenuItems={({ row, closeMenu }) => [
            <MenuItem
              key="edit"
              onClick={() => {
                closeMenu();
                setEditorPost(row.original as unknown as Post);
                setEditorOpen(true);
              }}
              data-testid={`${TESTID}-row-edit`}
            >
              <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
              Edit / view
            </MenuItem>,
            (row.original.url || row.original.sourceUrl) ? (
              <MenuItem
                key="open"
                onClick={() => {
                  closeMenu();
                  window.open(row.original.url || row.original.sourceUrl, '_blank', 'noopener');
                }}
                data-testid={`${TESTID}-row-open-original`}
              >
                <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
                Open original
              </MenuItem>
            ) : null,
            <MenuItem
              key="delete"
              onClick={() => {
                closeMenu();
                if (row.original.id) handleDeleteOne(row.original.id);
              }}
              sx={{ color: 'error.main' }}
              data-testid={`${TESTID}-row-delete`}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>,
          ].filter(Boolean) as React.ReactElement[]}
          renderEmptyRowsFallback={() => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No posts scraped yet.{' '}
                {isTwitterPlatform
                  ? 'Click "Sync X posts" to fetch from the connected X account.'
                  : isRedditPlatform
                    ? 'Click "Sync Reddit posts" to fetch from the connected Reddit account.'
                  : profileUrl
                  ? `Click "Scrape more" to fetch from ${platformName}.`
                  : 'Set a profile URL in the Config tab and run Scrape more.'}
              </Typography>
            </Box>
          )}
        />
      )}

      <ConfirmActionDialog
        open={scrapeOpen}
        projectId={projectId}
        title={
          isTwitterPlatform
            ? 'Sync X posts'
            : isRedditPlatform
              ? 'Sync Reddit posts'
              : `Scrape more posts from ${platformName}`
        }
        description={
          isTwitterPlatform
            ? 'Fetches recent posts from the connected X account through the official X API and appends them to the corpus. No AI processing runs.'
            : isRedditPlatform
              ? 'Fetches recent submitted posts from the connected Reddit account through the official Reddit Data API and appends them to the corpus. No AI processing runs.'
            : 'Fetches additional posts via BrightData and appends them to the corpus. No AI processing — voice and post_examples remain untouched.'
        }
        mode="scrape"
        defaultMaxPosts={isNativeSyncPlatform ? 25 : undefined}
        maxPostsLabel={isNativeSyncPlatform ? 'Max posts to sync' : undefined}
        maxPostsHelp={
          isTwitterPlatform
            ? 'X API sync fetches up to N recent posts from the connected account. Media attachments are mirrored into Segmently assets when available.'
            : isRedditPlatform
              ? 'Reddit API sync fetches up to N recent submitted posts from the connected account. Image and gallery media are mirrored into Segmently assets when available.'
            : undefined
        }
        showDateRange={isNativeSyncPlatform ? false : undefined}
        confirmLabel={isNativeSyncPlatform ? 'Sync posts' : 'Start scrape'}
        onConfirm={handleScrapeConfirm}
        onClose={() => setScrapeOpen(false)}
      />

      <ConfirmActionDialog
        open={reanalyzeOpen}
        projectId={projectId}
        title={`Re-analyze ${platformName} voice`}
        description="AI re-derives voice + post_examples from the current corpus (excluding any posts you've deleted). Author-level identity, posts list, and topic pillars are NOT touched."
        mode="ai"
        defaultModel={projectDefaultModel.value}
        confirmLabel="Re-analyze"
        onConfirm={handleReanalyzeConfirm}
        onClose={() => setReanalyzeOpen(false)}
      />

      <AuthorPostEditorDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditorPost(null);
        }}
        projectId={projectId}
        userId={userId}
        authorId={authorId}
        platformId={platformId}
        platform={platformName.toLowerCase()}
        authorBasePath={basePath === 'external_authors' ? 'external_authors' : 'authors'}
        post={editorPost}
      />
    </Box>
  );
};

export default PlatformPostsTable;
